import React from 'react';
import {OUTCOMES} from '../dice';
import {vtmT} from '../i18n';

const dieKind = (value, hunger) => {
  if (hunger) {
    if (value === 10) return 'messy';
    if (value === 1) return 'bestial';
  } else if (value === 10) {
    return 'crit';
  }
  return value >= 6 ? 'success' : 'fail';
};

/** d10 en losange. Cliquable (bouton) quand `onClick` est fourni. */
export const VtmDie = ({value, hunger = false, selected = false, onClick, small = false, rerolled = false}) => {
  const className = `vtmDie ${hunger ? 'hunger' : 'regular'} ${dieKind(value, hunger)}${selected ? ' selected' : ''}${small ? ' small' : ''}${rerolled ? ' rerolled' : ''}`;
  const face = <span className="vtmDieFace">{value}</span>;
  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick} aria-pressed={selected} aria-label={`${value}`}>
        {face}
      </button>
    );
  }
  return <span className={className} role="img" aria-label={`${value}`}>{face}</span>;
};

const OUTCOME_TONE = {
  [OUTCOMES.MESSY_CRITICAL]: 'blood',
  [OUTCOMES.BESTIAL_FAILURE]: 'blood',
  [OUTCOMES.CRITICAL_WIN]: 'gold',
  [OUTCOMES.WIN]: 'bone',
  [OUTCOMES.FAILURE]: 'ash',
  [OUTCOMES.TOTAL_FAILURE]: 'ash',
};

export const outcomeTone = (outcome) => OUTCOME_TONE[outcome] || 'bone';

/** Verdict lisible ; sans difficulté, le nombre de succès et les indicateurs pour le MJ. */
export const outcomeText = (record) => {
  if (record.autoFailure) return vtmT('autoFailure');
  if (record.outcome) return vtmT(`outcome_${record.outcome}`);
  return `${record.successes} ${vtmT('successes')}`;
};

export const outcomeHints = (record) => {
  if (record.outcome) return [];
  return [
    record.messyIfWin && vtmT('messyIfWin'),
    record.bestialIfFail && vtmT('bestialIfFail'),
  ].filter(Boolean);
};

export const rouseText = (rouse) => {
  if (!rouse) return '';
  if (rouse.success) return vtmT('rouseSuccess');
  if (rouse.hungerFrenzyRisk) return vtmT('rouseFrenzyRisk');
  return vtmT('rouseFail', {value: rouse.newHunger});
};

/** « 3 → 7 · le sang répond, Soif stable » */
export const rouseDetail = (rouse) => `${rouse.dice.join(' → ')} · ${rouseText(rouse)}`;

/** Tests d'exaltation d'un pouvoir, un par ligne. */
export const PowerRouseLines = ({checks, className}) => checks.map((check, index) => (
  <p key={index} className={className}>
    {vtmT('powerRouse', {index: index + 1, count: checks.length})} : {rouseDetail(check)}
  </p>
));

/**
 * Résultat d'un jet de réserve : dés normaux (sélectionnables pour la relance), dés de Soif, verdict.
 * @param {Object} props.record jet au format createRollRecord
 * @param {Number[]} [props.selection] index des dés normaux sélectionnés
 * @param {Function} [props.onToggleDie] active la sélection
 */
export const VtmPoolResult = ({record, selection = [], onToggleDie}) => (
  <div className="vtmPoolResult">
    {record.regular.length > 0 && (
      <div className="vtmDiceGroup">
        <div className="vtmDiceGroupTitle">
          {vtmT('regularDice')}
          {onToggleDie && <span className="vtmDiceGroupHint"> · {vtmT('regularDiceHint')}</span>}
        </div>
        <div className="vtmDiceRow">
          {record.regular.map((value, index) => (
            <VtmDie
              key={index}
              value={value}
              selected={selection.includes(index)}
              rerolled={record.rerolledIndexes.includes(index)}
              onClick={onToggleDie ? () => onToggleDie(index) : undefined}
            />
          ))}
        </div>
      </div>
    )}
    {record.hunger.length > 0 && (
      <div className="vtmDiceGroup">
        <div className="vtmDiceGroupTitle hunger">
          {vtmT('hungerDice')} <span className="vtmDiceGroupHint">· {vtmT('hungerDiceHint')}</span>
        </div>
        <div className="vtmDiceRow">
          {record.hunger.map((value, index) => <VtmDie key={index} value={value} hunger />)}
        </div>
      </div>
    )}
    <div className="vtmVerdict">
      <div className="vtmVerdictCount">
        <span className="vtmVerdictNumber">{record.successes ?? 0}</span>
        <span className="vtmVerdictLabel">{vtmT('successes')}</span>
      </div>
      <div className="vtmVerdictText">
        <span className={`vtmVerdictOutcome ${outcomeTone(record.outcome)}`}>{outcomeText(record)}</span>
        <span className="vtmVerdictDetail">
          {record.margin !== null && vtmT('margin', {value: `${record.margin >= 0 ? '+' : ''}${record.margin}`})}
          {record.difficulty === null && !record.autoFailure && `${vtmT('difficulty')} ${vtmT('difficultyUnknown')}`}
          {outcomeHints(record).map(hint => ` · ${hint}`)}
          {record.willpowerRerolled && ` · ${vtmT('willpowerRerolled')}`}
        </span>
      </div>
    </div>
    <PowerRouseLines checks={record.powerRouses || []} className="vtmRouseLine" />
    {record.rouse && (
      <p className="vtmRouseLine">{vtmT('bloodSurge')} : {rouseDetail(record.rouse)}</p>
    )}
  </div>
);
